import { AfterViewInit, Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { MediaChange, MediaObserver } from '@ngbracket/ngx-layout';
import { MatDrawerMode, MatSidenav } from '@angular/material/sidenav';
import { filter, take } from 'rxjs/operators';
import { MenuItems } from '@app/menuItems';
import { NavigationEnd, Router } from '@angular/router';

import { AppHelper } from '@app/core/classes/app-helper';
import { PermissionsService } from '@app/core/permissions/permissions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@app/core';

type MenuState = 'large' | 'small';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss']
})
export class ShellComponent implements OnInit, AfterViewInit {
  menuItems = new MenuItems();
  active: string;
  @ViewChild('sidenav') sidenav!: MatSidenav;
  menuSizeState: MenuState = 'large';
  mode: MatDrawerMode = 'side';
  private isMobile = false;
  isMenuSizeStateClicked = false;
  constructor(
    protected media: MediaObserver,
    protected translateService: TranslateService,
    protected router: Router,
    private permissionsService: PermissionsService,
    private languageService: LanguageService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    // Monitor screen size changes
    this.media
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changes: MediaChange[]) => {
        const isMobileScreen = changes.some(change => change.mqAlias === 'xs' || change.mqAlias === 'sm');

        if (isMobileScreen && !this.isMobile) {
          // Switched to mobile - use small menu
          this.isMobile = true;
          this.menuSizeState = 'small';
        } else if (!isMobileScreen && this.isMobile) {
          // Switched to desktop - use large menu
          this.isMobile = false;
          this.menuSizeState = 'large';
          this.isMenuSizeStateClicked = false;
          this.mode = 'side';
        }

        // Recalculate container width after state change
        setTimeout(() => AppHelper.calcMainContainerWidth(), 300);
      });

    // Load permissions once on init
    this.permissionsService
      .permissionsRequest()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this.permissionsSubscription();
  }

  /**
   * Subscribe to permission refresh events
   */
  permissionsSubscription() {
    this.permissionsService.refresh.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.permissionsService
        .permissionsRequest({ refresh: true })
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe();
    });
  }

  get isEnglish(): boolean {
    return this.languageService.isEnglish;
  }

  toggle(index: number) {
    this.menuItems.items[index]['active'] = !this.menuItems.items[index]['active'];
  }

  ngAfterViewInit() {
    AppHelper.calcMainContainerWidth();
    AppHelper.checkWindowResize();

    // Track active route
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        this.active = (event as NavigationEnd).url;
      });
  }

  /**
   * Toggle menu size between small and large
   * @param size - Optional specific size to set
   */
  toggleMenuSize(size?: MenuState) {
    if (size) {
      // Set specific size
      this.menuSizeState = size;
    } else {
      // Toggle between small and large
      this.menuSizeState = this.menuSizeState === 'large' ? 'small' : 'large';
      if (this.menuSizeState === 'large') {
        this.isMenuSizeStateClicked = true;
        this.mode = 'over';
      } else {
        this.isMenuSizeStateClicked = false;
        this.mode = 'side';
      }
    }

    // Recalculate container width after animation
    setTimeout(() => {
      AppHelper.calcMainContainerWidth();
    }, 300);
  }

  /**
   * Get child routes from menu item
   * @param menuItem - Menu item with potential children
   */
  getChildrenRoutes(menuItem: any): string[] {
    const childLinks: string[] = [];
    if (menuItem.children) {
      menuItem.children.forEach((child: any) => {
        if (child.url) {
          childLinks.push(child.url);
        }
      });
    }
    return childLinks;
  }

  /**
   * Check if current route matches any child route
   * @param menuItem - Menu item to check
   */
  checkCurrentRoute(menuItem: any): boolean {
    const currentUrl = this.router.url;
    return this.getChildrenRoutes(menuItem).some((url: string) => currentUrl === url);
  }

  /**
   * Check if current route includes menu item title
   * @param menuItem - Menu item to check
   */
  checkChildParentRoute(menuItem: any): boolean {
    return this.router.url.includes(menuItem.title);
  }

  /**
   * Check if user has specific permission
   * @param key - Permission key
   */
  checkPermission(key?: string): boolean {
    return !!this.permissionsService.getPermissions(key);
  }

  /**
   * Trigger permission refresh
   */
  refreshPermissions() {
    this.permissionsService.refresh.next(null);
  }

  onNavigate() {
    if (this.menuSizeState === 'large' && this.isMenuSizeStateClicked) {
      this.toggleMenuSize();
    }
  }

  onOverlay() {
    if (this.menuSizeState === 'large' && this.isMenuSizeStateClicked) {
      this.toggleMenuSize();
    }
  }
}
